import { useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { usePatchLoanAssetsMutation } from '@/apis/extended/loanApplicationApi';
import type {
  LoanApplication,
  LoanAssetLine,
} from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

export function AssetsSection({ application, readOnly }: Props) {
  const [patch, { isLoading }] = usePatchLoanAssetsMutation();

  const initialLines = useMemo(
    () =>
      (application.assetLines?.length
        ? application.assetLines
        : [{ institutionName: '', accountType: '', balance: 0 }]
      ).map((a) => ({ ...a })),
    [application._id, application.assetLines, application.updatedAt]
  );

  const {
    draft: lines,
    setDraft: setLines,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'assets',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initialLines,
    readOnly,
  });

  const saveSection = async () => {
    if (formReadOnly) return;
    try {
      await patch({
        id: application._id,
        assetLines: lines,
      }).unwrap();
      markSaved(lines);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Liquid assets and accounts.
      </Typography>
      {lines.map((row, idx) => (
        <Grid container spacing={2} key={row._id ?? idx} sx={{ mb: 2 }}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Institution"
              value={row.institutionName}
              disabled={disabled}
              onChange={(e) => {
                const next = [...lines];
                next[idx] = { ...row, institutionName: e.target.value };
                setLines(next);
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Account type"
              value={row.accountType}
              disabled={disabled}
              onChange={(e) => {
                const next = [...lines];
                next[idx] = { ...row, accountType: e.target.value };
                setLines(next);
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Balance"
              type="number"
              value={row.balance}
              disabled={disabled}
              onChange={(e) => {
                const next = [...lines];
                next[idx] = {
                  ...row,
                  balance: Number(e.target.value) || 0,
                };
                setLines(next);
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={1}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {!formReadOnly && (
              <IconButton
                aria-label="remove"
                onClick={() => setLines((L) => L.filter((_, i) => i !== idx))}
                disabled={lines.length <= 1}
              >
                <i className="fa-light fa-trash" />
              </IconButton>
            )}
          </Grid>
        </Grid>
      ))}
      {!formReadOnly && (
        <Box sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            onClick={() =>
              setLines((L) => [
                ...L,
                { institutionName: '', accountType: '', balance: 0 },
              ])
            }
          >
            Add account
          </Button>
        </Box>
      )}
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save assets"
        onSave={() => void saveSection()}
        onCancel={resetDraft}
      />
    </div>
  );
}
